import { motion } from "framer-motion"; // Import Framer Motion
import {
  EllipsisVertical,
  LogOut,
  Settings,
  Terminal,
  TypeOutline,
  Users,
} from "lucide-react";
import { LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useToken } from "@/hooks/useToken";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useEffect } from "react";
import { useMerchantDetail } from "@/hooks/useMerchantDetail";

type TSidebar = {
  name: string;
  link: string;
  isVisibleTo: boolean;
  icon: JSX.Element;
};

function Sidebar({
  isOpen,
  open,
  close,
}: {
  isOpen?: boolean;
  open: () => void;
  close: () => void;
}) {
  const { removeMerchantLogin, merchantLogin } = useMerchantDetail();
  const { removeToken } = useToken();
  const navigate = useNavigate();
  const getInitial = () => merchantLogin?.name?.substring(0, 2).toUpperCase();

  const authorizeToView = (roles: string[]) => roles.includes("all");

  const sidebarLink: TSidebar[] = [
    {
      name: "Dashboard",
      link: "/merchant-dashboard",
      icon: <LayoutGrid />,
      isVisibleTo: authorizeToView(["all"]),
    },
    {
      name: "Transactions",
      icon: <TypeOutline />,
      link: "/merchant-dashboard/transactions",
      isVisibleTo: authorizeToView(["all"]),
    },
    {
      name: "Terminals",
      icon: <Terminal />,
      link: "/merchant-dashboard/terminals",
      isVisibleTo: authorizeToView(["all"]),
    },
    {
      name: "Users",
      icon: <Users />,
      link: "/merchant-dashboard/users",
      isVisibleTo: authorizeToView(["all"]),
    },

    {
      name: "Change Password",
      icon: <Settings />,
      link: "/merchant-dashboard/change-password",
      isVisibleTo: authorizeToView(["all"]),
    },
  ];

  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (isMobile) close();
    else open();
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.section
      initial={{ x: "-100%" }} // Start off-screen
      animate={{ x: isOpen ? 0 : "-100%" }} // Slide-in when open
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={cn(
        "fixed inset-y-0 left-0 z-[2] h-full w-[250px] border-r border-muted bg-white pb-[30px] pt-[100px]"
      )}
    >
      <div className="flex h-full flex-col justify-between">
        <div className="flex flex-col gap-y-1">
          {sidebarLink.map(
            (link, idx) =>
              link.isVisibleTo && (
                <SideBarLink
                  key={idx}
                  link={link}
                  currentLocation={location.pathname}
                />
              )
          )}
        </div>

        <div className="flex flex-col space-y-5 px-4">
          <Button
            onClick={() => {
              removeToken();
              removeMerchantLogin();
              navigate("/");
            }}
            className="justify-start border-none bg-transparent px-4 text-red-500 shadow-none hover:bg-transparent"
          >
            <LogOut />
            <span>Log Out</span>
          </Button>

          <div className="relative flex items-center gap-2 rounded-lg bg-primary-500 p-4">
            <Button
              className="absolute right-1 top-0 p-0 hover:scale-110 hover:bg-transparent"
              variant={"ghost"}
            >
              <EllipsisVertical size={10} className="text-white" />
            </Button>
            {merchantLogin?.image ? (
              <div className="size-10 rounded-full">
                <img
                  src={merchantLogin.image}
                  alt="ProfileImage"
                  className="object-cover size-10  rounded-full"
                />
              </div>
            ) : (
              <div className="grid size-10 place-content-center rounded-full bg-muted text-primary-500">
                {getInitial()}
              </div>
            )}
            <div className="text-white">
              <div className="text-xs font-medium">
                {merchantLogin?.name ?? ""}
              </div>
              <div className="text-[10px]">{merchantLogin?.email ?? ""}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

const SideBarLink = ({
  link,
  currentLocation,
}: {
  link: TSidebar;
  currentLocation: string;
}) => {
  return (
    <Link
      to={link.link}
      className={cn(
        "flex items-center space-x-4 py-2",
        currentLocation === link.link && "text-primary-500"
      )}
    >
      <div
        className={cn(
          "h-[32px] w-1 rounded-r",
          currentLocation === link.link && "bg-primary-500"
        )}
      />
      <div className="">{link.icon}</div>
      <div>{link.name}</div>
    </Link>
  );
};

export default Sidebar;
